import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const PrivacyPolicyPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const custom = settings?.privacyContent;

  const sections = custom?.sections?.length
    ? custom.sections.map((s: { title: { en: string; ka: string }; text: { en: string; ka: string } }) => ({
        title: localize(s.title),
        text: localize(s.text),
      }))
    : [
        { title: t.legal.privacy.collectTitle, text: t.legal.privacy.collectText },
        { title: t.legal.privacy.useTitle, text: t.legal.privacy.useText },
        { title: t.legal.privacy.protectionTitle, text: t.legal.privacy.protectionText },
        { title: t.legal.privacy.rightsTitle, text: t.legal.privacy.rightsText },
        { title: t.legal.privacy.contactTitle, text: t.legal.privacy.contactText },
      ];

  const intro = custom?.intro ? localize(custom.intro) : t.legal.privacy.intro;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.legal.privacyPolicy}
          </h1>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">
            {t.legal.lastUpdated}
          </p>
        </motion.div>

        {intro && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-10 p-6 sm:p-8 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a]"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center shrink-0">
                <ShieldCheck size={18} className="text-black/40 dark:text-white/40" />
              </div>
              <p className="text-sm sm:text-base text-black/60 dark:text-white/60 font-light leading-relaxed">
                {intro}
              </p>
            </div>
          </motion.div>
        )}

        <div className="space-y-8">
          {sections.map((section: { title: string; text: string }, i: number) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-6 h-px bg-black/10 dark:bg-white/10" />
                <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                  {section.title}
                </h2>
              </div>
              <p className="text-sm sm:text-[15px] text-black/55 dark:text-white/55 font-light leading-[1.8] pl-9">
                {section.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
